/**
 * Os avisos que não chegaram a ninguém.
 *
 * Este ecrã não existe para ser bonito: existe para que uma mensagem por
 * entregar tenha um dono. Até agora falhava em silêncio — o envio escrevia
 * `failed` na base e a vida continuava. Num só mês ficaram 25 avisos por
 * entregar, entre eles parcelas libertadas de centenas de milhares de kwanzas
 * e defeitos apontados pelo cliente.
 *
 * A acção normal aqui é pegar no telefone. "Reenviar" só resolve quando a
 * causa era do fornecedor e entretanto foi corrigida; nos outros casos
 * alguém liga, escreve o que disse, e a linha sai da fila.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, PhoneOff, Send, Check, Phone } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';

const dt = (v) => (v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Africa/Luanda' }) : '—');

const SmsFalhadasPage = () => {
  const { success, error } = useToast();
  const [dados, setDados] = useState(null);
  const [verTratadas, setVerTratadas] = useState(false);
  const [aTratar, setATratar] = useState(null);
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(() => {
    api.get(`/sms/falhadas${verTratadas ? '?tratadas=1' : ''}`)
      .then((r) => setDados(r.data))
      .catch(() => { setDados({ mensagens: [], por_tratar: 0 }); error('Erro ao carregar a fila.'); });
  }, [verTratadas, error]);
  useEffect(() => { carregar(); }, [carregar]);

  const tratar = async () => {
    setBusy(true);
    try {
      await api.post(`/sms/falhadas/${aTratar.sms_id}/tratar`, { nota: nota.trim() });
      success('Marcada como tratada.');
      setATratar(null); setNota(''); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível marcar.');
    } finally { setBusy(false); }
  };

  const reenviar = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sms/falhadas/${m.sms_id}/reenviar`);
      if (data.sucesso) success('Reenviada — desta vez saiu.');
      else error(`Voltou a falhar${data.erro ? `: ${data.erro}` : '.'}`);
      carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível reenviar.');
    } finally { setBusy(false); }
  };

  if (!dados) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <PhoneOff className="w-6 h-6 text-amber-400" /> Avisos por entregar
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Mensagens que a plataforma tentou enviar e não saíram. Quem está do outro
          lado não sabe o que devia saber — que a obra foi paga, que a parcela foi
          libertada, que o cliente apontou defeitos. A acção normal é telefonar.
        </p>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <button onClick={() => setVerTratadas(false)}
          className={`min-h-[40px] px-4 rounded-lg text-sm font-semibold ${!verTratadas
            ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-300'}`}>
          Por tratar {dados.por_tratar > 0 && `(${dados.por_tratar})`}
        </button>
        <button onClick={() => setVerTratadas(true)}
          className={`min-h-[40px] px-4 rounded-lg text-sm font-semibold ${verTratadas
            ? 'bg-slate-700 text-white' : 'bg-slate-800 text-slate-300'}`}>
          Já tratadas
        </button>
      </div>

      {dados.mensagens.length === 0 ? (
        <div className="p-6 rounded-xl border border-slate-800 bg-slate-900 text-center text-slate-400 text-sm">
          {verTratadas ? 'Ainda não foi tratada nenhuma.' : 'Nada por entregar. Todos os avisos saíram.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {dados.mensagens.map((m) => (
            <li key={m.sms_id} className="p-3 rounded-xl border border-slate-800 bg-slate-900 space-y-2">
              <div className="flex gap-3 items-start flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-slate-200 font-semibold">
                    {m.full_name || 'Destinatário desconhecido'}
                    <span className="text-slate-500 font-normal"> · {m.phone_number}</span>
                  </div>
                  <div className="text-xs text-slate-500">{dt(m.created_at)}</div>
                </div>
                {/* O número em grande e clicável: quem está neste ecrã vai
                    telefonar, e obrigá-lo a copiar à mão é obrigá-lo a errar. */}
                <a href={`tel:${m.phone_number}`}
                  className="min-h-[40px] px-3 rounded-lg bg-slate-800 text-slate-200 text-sm inline-flex items-center gap-1.5">
                  <Phone className="w-4 h-4" /> Ligar
                </a>
              </div>

              <p className="text-sm text-slate-300 bg-slate-950 border border-slate-800 rounded-lg p-2">
                {m.message_text}
              </p>

              {m.tratada_em ? (
                <div className="text-xs text-emerald-500">
                  Tratada a {dt(m.tratada_em)}{m.tratada_por_nome ? ` por ${m.tratada_por_nome}` : ''}
                  {m.tratada_nota && <span className="block text-slate-400 mt-0.5">{m.tratada_nota}</span>}
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => { setATratar(m); setNota(''); }} disabled={busy}
                    className="min-h-[40px] px-3 rounded-lg bg-emerald-700 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
                    <Check className="w-4 h-4" /> Já avisei
                  </button>
                  <button onClick={() => reenviar(m)} disabled={busy}
                    className="min-h-[40px] px-3 rounded-lg border border-slate-700 text-slate-300 text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
                    <Send className="w-4 h-4" /> Tentar outra vez
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {aTratar && (
        <div className="fixed inset-0 z-50 bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label="Marcar como tratada">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
            <h2 className="text-white font-bold">Como é que a pessoa foi avisada?</h2>
            <p className="text-sm text-slate-400">
              {aTratar.full_name || aTratar.phone_number} — {aTratar.message_text}
            </p>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={3}
              placeholder="Ex.: telefonei e confirmei que já sabia da libertação"
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm" />
            <div className="flex gap-2">
              <button onClick={() => { setATratar(null); setNota(''); }}
                className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Voltar</button>
              <button onClick={tratar} disabled={busy || !nota.trim()}
                className="flex-1 min-h-[44px] rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-40">
                Marcar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsFalhadasPage;
